import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { UnitPosition } from '../lib/types'
import { POSITION_ORDER, slotLabel } from '../lib/rules'

/**
 * Commissioner scoring controls.
 *
 * Position weights multiply the league's view of a unit score. The raw score
 * stays global, so changing a weight here never touches another league — and
 * settled weeks are rescored immediately so the standings stay honest.
 *
 * Baselines are the real 2024-25 averages, so the share bar shows a
 * commissioner what their lineup actually looks like before they save.
 */
const BASELINE: Record<UnitPosition, number> = {
  QB: 16.55, RB: 20.02, WR: 26.31, TE: 10.18, K: 8.24, DEF: 5.97,
}

interface Preset {
  id: string
  name: string
  blurb: string
  weights: Record<UnitPosition, number>
}

type Weights = Record<UnitPosition, number>

export default function ScoringSettings({
  leagueId, isCommissioner, initial, onSaved,
}: {
  leagueId: string
  isCommissioner: boolean
  initial: Weights
  onSaved?: (w: Weights) => void
}) {
  const [w, setW] = useState<Weights>(initial)
  const [presets, setPresets] = useState<Preset[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    supabase.from('scoring_presets').select('*').order('sort_order')
      .then(({ data }) => setPresets((data ?? []) as Preset[]))
  }, [])

  const shares = useMemo(() => {
    const vals = POSITION_ORDER.map((p) => BASELINE[p] * (w[p] ?? 1))
    const total = vals.reduce((a, b) => a + b, 0) || 1
    return Object.fromEntries(
      POSITION_ORDER.map((p, i) => [p, (100 * vals[i]) / total]),
    ) as Record<UnitPosition, number>
  }, [w])

  const dirty = POSITION_ORDER.some((p) => w[p] !== initial[p])

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null)
    const { error } = await supabase.rpc('set_position_weights', {
      p_league_id: leagueId,
      p_weights: w,
    })
    setBusy(false)
    if (error) return setErr(error.message)
    setMsg('Saved. Every week already played has been rescored.')
    onSaved?.(w)
  }

  return (
    <div className="scoring">
      <header className="scoring-head">
        <div>
          <h3>Position weights</h3>
          <p className="muted">
            Multiply what each position is worth in this league. A whole WR room
            outscores a defense four to one by default — these dial that in.
          </p>
        </div>
      </header>

      <div className="share-bar" role="img"
           aria-label="Share of an average lineup total by position">
        {POSITION_ORDER.map((p) => (
          <div
            key={p}
            className={`share share-${p}`}
            style={{ width: `${shares[p]}%` }}
            title={`${slotLabel(p)} — ${shares[p].toFixed(1)}% of an average lineup`}
          >
            {shares[p] > 7 && <span>{slotLabel(p)}</span>}
          </div>
        ))}
      </div>
      <p className="muted small">
        Share of an average lineup total, using 2024–25 scoring.
      </p>

      <div className="weights">
        {POSITION_ORDER.map((p) => (
          <label key={p} className="weight">
            <span className="wlabel">
              {slotLabel(p)}
              <em>{shares[p].toFixed(0)}% of a lineup</em>
            </span>
            <input
              type="range" min="0" max="3" step="0.05"
              value={w[p] ?? 1}
              disabled={!isCommissioner}
              onChange={(e) => setW({ ...w, [p]: Number(e.target.value) })}
            />
            <output>{(w[p] ?? 1).toFixed(2)}×</output>
          </label>
        ))}
      </div>

      <div className="presets">
        <h4>Presets</h4>
        <div className="preset-grid">
          {presets.map((p) => (
            <button
              key={p.id}
              className="preset"
              disabled={!isCommissioner}
              onClick={() => setW(p.weights)}
            >
              <strong>{p.name}</strong>
              <span>{p.blurb}</span>
            </button>
          ))}
        </div>
      </div>

      {isCommissioner ? (
        <div className="row gap scoring-actions">
          <button className="btn btn-primary" onClick={save} disabled={busy || !dirty}>
            {busy ? 'Rescoring…' : dirty ? 'Save and rescore' : 'No changes'}
          </button>
          {dirty && (
            <button className="btn btn-ghost" onClick={() => setW(initial)}>
              Reset
            </button>
          )}
        </div>
      ) : (
        <p className="muted small">Only the commissioner can change scoring.</p>
      )}

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="error">{err}</p>}
    </div>
  )
}
